

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { openStream, type Frame, type StreamStatus } from "./stream";
import { decodeHeartbeat } from "./proto";

export type DeviceLive = {
  lastSeenAt: number;
  actuators?: { pin: number; on: boolean }[];
  uptimeS?: number;
  rssiDbm?: number;
  bootCount?: number;
};

const DEFAULT_ONLINE_WINDOW_MS = 90_000;

const ACTIVITY_MAX = 200;

export type ActivityEvent =
  | { id: string; kind: "actuator"; deviceId: string; pin: number; on: boolean; at: number }
  | { id: string; kind: "online"; deviceId: string; at: number }
  | { id: string; kind: "offline"; deviceId: string; at: number };

type State = {
  devices: Record<string, DeviceLive>;
  events: ActivityEvent[];
};

type Action =
  | { type: "frame"; frame: Frame }
  | { type: "tick"  };

const INITIAL_STATE: State = { devices: {}, events: [] };

function reducer(state: State, action: Action): State {
  if (action.type === "tick") return state;
  const f = action.frame;
  const prev = state.devices[f.device_id] ?? { lastSeenAt: 0 };

  const isLiveness = f.kind === "hb" || f.kind === "info" || f.kind === "status";

  const stamped = f.at ? Date.parse(f.at) : NaN;
  const frameAt = Number.isFinite(stamped) ? stamped : Date.now();

  const lastSeenAt = isLiveness
    ? Math.max(prev.lastSeenAt, frameAt)
    : prev.lastSeenAt;

  const next: DeviceLive = { ...prev, lastSeenAt };
  const newEvents: ActivityEvent[] = [];

  const wasOnline =
    prev.lastSeenAt > 0 && Date.now() - prev.lastSeenAt < DEFAULT_ONLINE_WINDOW_MS;
  const isNowOnline = Date.now() - lastSeenAt < DEFAULT_ONLINE_WINDOW_MS;
  if (!wasOnline && isNowOnline) {
    newEvents.push({
      id: eventId(f, "online"),
      kind: "online",
      deviceId: f.device_id,
      at: frameAt,
    });
  }

  if (f.kind === "hb") {
    try {
      const hb = decodeHeartbeat(f.payload);
      const newActuators = (hb.actuators ?? []).map((a, i) => ({
        pin: a.pin ?? i,
        on: !!a.on,
      }));

      if (prev.actuators) {
        for (const a of newActuators) {
          const before = prev.actuators.find((p) => p.pin === a.pin);
          if (before && before.on !== a.on) {
            newEvents.push({
              id: eventId(f, `act-${a.pin}`),
              kind: "actuator",
              deviceId: f.device_id,
              pin: a.pin,
              on: a.on,
              at: frameAt,
            });
          }
        }
      }

      next.actuators = newActuators;
      if (hb.uptimeS != null) next.uptimeS = hb.uptimeS as number;

      if (hb.rssiDbm != null && (hb.rssiDbm as number) !== 0) {
        next.rssiDbm = hb.rssiDbm as number;
      }
      if (hb.bootCount != null) next.bootCount = hb.bootCount as number;
    } catch {

    }
  }

  const events =
    newEvents.length === 0
      ? state.events
      : [...newEvents, ...state.events].slice(0, ACTIVITY_MAX);

  return {
    devices: { ...state.devices, [f.device_id]: next },
    events,
  };
}

function eventId(f: Frame, suffix: string): string {
  const base = f.id != null ? `f${f.id}` : `${f.device_id}-${f.at}`;
  return `${base}-${suffix}`;
}

type LiveCtx = {
  state: State;
  status: StreamStatus;

  tick: number;
};

const Ctx = createContext<LiveCtx | null>(null);

export function LiveProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [status, setStatus] = useState<StreamStatus>("connecting");
  const [tick, setTick] = useState(0);

  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  useEffect(() => {
    const handle = openStream(
      "/stream",
      (f) => dispatchRef.current({ type: "frame", frame: f }),
      { onStatus: setStatus },
    );
    return () => handle.close();
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 15_000);
    return () => window.clearInterval(id);
  }, []);

  const value = useMemo<LiveCtx>(
    () => ({ state, status, tick }),
    [state, status, tick],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function useLiveCtx(): LiveCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useLive* hooks require <LiveProvider>");
  return v;
}

export function useDeviceLive(deviceId: string): DeviceLive | undefined {
  const { state } = useLiveCtx();
  return state.devices[deviceId];
}

export function useAllLive(): Record<string, DeviceLive> {
  return useLiveCtx().state.devices;
}

export function useActivityEvents(): ActivityEvent[] {
  return useLiveCtx().state.events;
}

export function useLiveStatus(): StreamStatus {
  return useLiveCtx().status;
}

export function isOnline(
  live: DeviceLive | undefined,
  windowMs = DEFAULT_ONLINE_WINDOW_MS,
): boolean {
  if (!live?.lastSeenAt) return false;
  return Date.now() - live.lastSeenAt < windowMs;
}
